/**
 * PATH: packages/ui/src/hooks/hubRouteMemory.helpers.ts
 * FILE_ID: UI-HOOK-HUB-ROUTE-MEMORY-HELPERS-001
 * PURPOSE: Pure I→O helpers backing per-hub "last sub-route" memory so a
 *          tenant sidebar / dock click resumes the screen the operator last
 *          had open in that hub (Schedule → last planner view, Email → last
 *          folder/thread) instead of always snapping back to the hub root.
 * WHY: Operator (2026-06-15): "when I click Schedule it jumps to the top;
 *      when I click Email it remembers the last one — why aren't these the
 *      same?". The fix is ONE shared primitive every tenant shell consumes,
 *      so the behaviour is identical across SAM / Newmark / the master SPA
 *      (build-it-once, no per-tenant customisation).
 * FLOW:
 *   ┌────────────────┐   ┌───────────────┐   ┌──────────────────────────┐
 *   │ location change│ → │ hubBucketOf   │ → │ record {bucket: fullPath} │
 *   └────────────────┘   └───────────────┘   └──────────────────────────┘
 *   ┌────────────────┐   ┌───────────────┐   ┌──────────────────────────┐
 *   │ nav item click │ → │ pickHubTarget │ → │ navigate(resumed | root)  │
 *   └────────────────┘   └───────────────┘   └──────────────────────────┘
 * DEPENDENCIES: none — pure functions, fully unit-tested.
 * RELATED:
 *   - ./useHubRouteMemory.ts  (localStorage + React hooks around these)
 *   - ./useLastRouteResume.ts (sibling: cold-boot resume; shares filtering)
 */

/** localStorage key prefix; one JSON map (`{bucket: fullPath}`) per scope. */
export const HUB_ROUTE_KEY_PREFIX = 'quotech:hub-route:'

export function hubRouteMapKey(scope: string): string {
  return `${HUB_ROUTE_KEY_PREFIX}${scope}`
}

/**
 * The hub "bucket" for a path = its first path segment (`/email/inbox` →
 * `/email`). Returns null for the bare root / empty path, which carry no
 * resumable hub state. Query strings are ignored for bucketing.
 */
export function hubBucketOf(path: string | null | undefined): string | null {
  if (typeof path !== 'string') return null
  const pathnameOnly = path.split('?')[0]
  const segments = pathnameOnly.split('/').filter(Boolean)
  if (segments.length === 0) return null
  return `/${segments[0]}`
}

/**
 * Segment-aware prefix test: true when `path` IS `prefix` or sits beneath it
 * at a path-segment boundary. Guards against `/billsX` matching `/bills`.
 * The query string on `path` is ignored for the comparison.
 */
export function startsWithSegment(
  path: string | null | undefined,
  prefix: string,
): boolean {
  if (typeof path !== 'string' || !prefix) return false
  const pathnameOnly = path.split('?')[0]
  if (pathnameOnly === prefix) return true
  return pathnameOnly.startsWith(`${prefix}/`)
}

/** Distinct nav targets that belong to the same first-segment bucket. */
function entriesInBucket(navTargets: string[], bucket: string): string[] {
  const seen = new Set<string>()
  for (const t of navTargets) {
    if (hubBucketOf(t) === bucket) seen.add(t)
  }
  return [...seen]
}

/**
 * Pure resume decision. Given the nav item the operator clicked (`to`), the
 * path remembered for that hub bucket (`stored`), and the full set of nav
 * targets (`navTargets`, used to tell single- from multi-entry hubs), return
 * the path to navigate to.
 *
 * Rules (all degrade safely to `to`):
 *   - No memory, or memory equals the click target → go to `to`.
 *   - Single-entry hub (one nav item owns the whole bucket, e.g. Email's
 *     `/email/inbox` owns all of `/email/*`) → resume anything under the
 *     bucket.
 *   - Multi-entry hub (several nav items share a bucket, e.g. Bills'
 *     `/bills/inbox` + `/bills/list`, or `/quotes` + `/quotes/builder`) →
 *     resume only when `to` is the most-specific nav entry that owns the
 *     stored path, so clicking "Quote Tracking" never lands inside the
 *     Quote Builder and vice-versa.
 */
export function pickHubTarget(args: {
  to: string
  stored: string | null | undefined
  navTargets: string[]
}): string {
  const { to, stored, navTargets } = args
  // Jobs is one list. A click on the rail opens that list, not the last job.
  if (to === '/jobs') return '/jobs'
  if (!stored) return to
  if (stored === to) return to

  const bucket = hubBucketOf(to)
  if (!bucket) return to
  // Memory is bucketed, but be defensive against a corrupted/foreign entry.
  if (!startsWithSegment(stored, bucket)) return to

  const entries = entriesInBucket(navTargets, bucket)

  // Single nav entry owns the entire bucket → resume freely under it.
  if (entries.length <= 1) return stored

  // Multi-entry bucket: the owner of `stored` is the longest nav entry that
  // is a segment-prefix of it. Only the owning entry resumes.
  let owner: string | null = null
  for (const entry of entries) {
    if (startsWithSegment(stored, entry)) {
      if (owner === null || entry.length > owner.length) owner = entry
    }
  }
  return owner === to ? stored : to
}
